import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Autocomplete,
  useTheme as useMuiTheme,
} from '@mui/material';

import { useActions } from '../../hooks/useActions';
import { useRuleStore } from '../../state/ruleStore';
import { truncateText } from '../../utils/actionUtils';
import InternalButton from 'components/InternalButton';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

interface ActionsStepProps {}

const ActionsStep: React.FC<ActionsStepProps> = () => {
  const theme = useThemeContext();
  const muiTheme = useMuiTheme();

  const { draft } = useRuleStore();
  const {
    actions,
    actionDefinitions,
    teams,
    loading,
    error,
    searchFilters,
    addAction,
    removeAction,
    updateAction,
    updateActionDynamicField,
    setSearchTerm,
    getFilteredActions,
    validateActions,
    validateActionDynamicFields,
    generateActionText,
    generateDynamicFields,
    loadActionDefinitions,
    loadTeams,
    createActionsInApi,
    hydrateActions,
    clearActions,
  } = useActions();

  const [showErrors, setShowErrors] = useState(false);

  // Carregar definições de ações quando o componente monta
  useEffect(() => {
    console.log('🔄 ActionsStep montado, carregando definições...');
    loadActionDefinitions();
  }, [loadActionDefinitions]);

  // Carregar equipes quando há ações que precisam
  useEffect(() => {
    const needsTeams = actions.some(
      action =>
        action.dynamicFields && Object.keys(action.dynamicFields).some(key => key.includes('team'))
    );

    if (needsTeams && teams.length === 0) {
      console.log('🔄 Carregando equipes para ações...');
      loadTeams();
    }
  }, [actions, teams.length, loadTeams]);

  // Hidratar ações existentes se estivermos editando
  useEffect(() => {
    if (draft.uuid && actions.length === 0) {
      console.log(`🔄 Hidratando ações da regra: ${draft.uuid}`);
      hydrateActions(draft.uuid as string);
    }
  }, [draft.uuid, actions.length, hydrateActions]);

  // Carregar equipes quando necessário
  useEffect(() => {
    const hasUserToTeamAction = actions.some(action => {
      if (!action.actionKey) return false;
      const actionDef = actionDefinitions.find(def => def.id.toString() === action.actionKey);
      return actionDef?.object_type === 'USER_TO_TEAM';
    });

    if (hasUserToTeamAction && teams.length === 0) {
      console.log('🔄 Carregando equipes para ações USER_TO_TEAM...');
      loadTeams();
    }
  }, [actions, actionDefinitions, teams.length, loadTeams]);

  return (
    <Box py={2}>
      {/* Título */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: theme.text.primary }}>
        Ações
      </Typography>

      {/* Descrição */}
      <Typography variant="body2" sx={{ mb: 3, color: theme.text.secondary }}>
        Quando as condições forem atendidas, as seguintes ações serão executadas:
      </Typography>

      {/* Lista de ações */}
      <Box sx={{ mb: 3 }}>
        {actions.map((action, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: `1px solid ${theme.text.secondary}20`,
              backgroundColor: theme.background.primary,
              borderRadius: 2,
            }}
          >
            {/* Cabeçalho da ação */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                  Ação {index + 1}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => removeAction(index)}
                sx={{ color: theme.error }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* ✅ Tipo de Ação com Autocomplete (busca LOCAL independente) */}
            <Autocomplete
              size="small"
              fullWidth
              options={actionDefinitions}
              value={actionDefinitions.find(def => def.id.toString() === action.actionKey) || null}
              onChange={(_, newValue) => {
                updateAction(index, 'actionKey', newValue ? newValue.id.toString() : '');
              }}
              getOptionLabel={option => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                      {truncateText(option.description, 60)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Tipo de Ação"
                  placeholder="Buscar ações..."
                  error={showErrors && !action.actionKey}
                  helperText={
                    showErrors && !action.actionKey ? 'Tipo de ação é obrigatório' : undefined
                  }
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-input': { color: theme.text.primary },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.text.primary,
                    },
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />

            {/* Campos dinâmicos baseados no config_schema */}
            {action.actionKey &&
              (() => {
                const selectedActionDef = actionDefinitions.find(
                  def => def.id.toString() === action.actionKey
                );

                if (!selectedActionDef?.config_schema) return null;

                const dynamicFields = generateDynamicFields(selectedActionDef);

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text.primary }}>
                      Configurações da Ação
                    </Typography>

                    {dynamicFields.map(field => {
                      // Campo para seleção de grupo temático
                      if (field.key === 'team_uuid' && field.type === 'string') {
                        return (
                          <FormControl key={field.key} fullWidth size="small">
                            <InputLabel>Selecionar Grupo Temático</InputLabel>
                            <Select
                              value={action.dynamicFields?.[field.key] || ''}
                              label="Selecionar Grupo Temático"
                              onChange={e =>
                                updateActionDynamicField(index, field.key, e.target.value)
                              }
                              disabled={loading}
                              sx={{
                                '& .MuiSelect-select': { color: theme.text.primary },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.text.primary,
                                },
                              }}
                            >
                              {loading ? (
                                <MenuItem disabled>Carregando grupos...</MenuItem>
                              ) : (
                                teams.map(team => (
                                  <MenuItem key={team.uuid} value={team.uuid}>
                                    {team.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                            {field.description && (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.text.secondary, mt: 0.5 }}
                              >
                                {field.description}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      // Campo para nome do grupo (criação)
                      if (field.key === 'team_name' && field.type === 'string') {
                        return (
                          <TextField
                            key={field.key}
                            fullWidth
                            size="small"
                            label="Nome do Grupo Temático"
                            placeholder={field.description}
                            value={action.dynamicFields?.[field.key] || ''}
                            onChange={e =>
                              updateActionDynamicField(index, field.key, e.target.value)
                            }
                            sx={{
                              '& .MuiInputBase-input': { color: theme.text.primary },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.text.primary,
                              },
                            }}
                          />
                        );
                      }

                      // Campo para auto_create (checkbox)
                      if (field.key === 'auto_create' && field.type === 'boolean') {
                        return (
                          <Box key={field.key} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={action.dynamicFields?.[field.key] || false}
                              onChange={e =>
                                updateActionDynamicField(index, field.key, e.target.checked)
                              }
                            />
                            <Typography variant="body2" sx={{ color: theme.text.primary }}>
                              {field.description || 'Criar automaticamente'}
                            </Typography>
                          </Box>
                        );
                      }

                      return null;
                    })}
                  </Box>
                );
              })()}
          </Paper>
        ))}
      </Box>

      {/* Botão adicionar ação */}
      <InternalButton
        variant="primary"
        icon={<AddIcon />}
        onClick={addAction}
        background={theme.primary}
      >
        Adicionar Ação
      </InternalButton>

      {/* Erros gerais */}
      {showErrors && !validateActions().isValid && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: theme.error, fontWeight: 500, mb: 1 }}>
            Por favor, corrija os seguintes erros:
          </Typography>
          {validateActions().errors.map((error, index) => (
            <Typography key={index} variant="body2" sx={{ color: theme.error, display: 'block' }}>
              • {error}
            </Typography>
          ))}
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.text.secondary }}>
            Carregando dados...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: theme.error }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export { ActionsStep };
export default ActionsStep;
